import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Button,
  useTheme
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ContactModal from './ContactModal';
import EditableContent from './EditableContent';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';
import byggnadsvardImage from '../assets/images/byggnadsvard.jpg';
import tradgardImage from '../assets/images/tradgard.jpg';
import fasadImage from '../assets/images/fasad.jpg';

// Animationsvarianter för karusellen
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000
  }),
  center: {
    x: 0
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 1000 : -1000
  })
};

// Tjänster data
const heroSlides = [
  {
    id: 1,
    title: 'Trädgårdsarbete',
    description: 'Vi hjälper både privatpersoner och företag med professionellt trädgårdsarbete.',
    image: tradgardImage
  },
  {
    id: 2,
    title: 'Byggprojekt',
    description: 'Vi skapar funktionella och vackra utemiljöer med kvalitet i fokus. Oavsett om du vill bygga en altan, pergola, attefallshus eller en annan tillbyggnad, hjälper vi dig att förverkliga din vision med gediget hantverk och smarta lösningar.',
    image: fasadImage
  },
  {
    id: 3,
    title: 'Byggnadsvård',
    description: 'Vi erbjuder professionell byggnadsvård med fokus på att bevara och restaurera byggnaders standard och kvalitet.',
    image: byggnadsvardImage
  }
];

const Home = () => {
  const theme = useTheme();
  const { isAuthenticated, isAdmin } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [[page, direction], setPage] = useState([0, 0]);
  const [slides, setSlides] = useState(heroSlides);

  // Hämta innehåll från databasen när komponenten laddas
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${config.api.baseUrl}/cms/pages/home`);
        if (!response.ok) {
          throw new Error('Kunde inte hämta innehåll');
        }
        
        const data = await response.json();
        if (data.content) {
          const content = JSON.parse(data.content);
          
          // Uppdatera slides med innehåll från databasen
          setSlides(currentSlides => {
            return currentSlides.map((slide, index) => {
              const slideNumber = index + 1;
              return {
                ...slide,
                title: content[`slide_${slideNumber}_title`] || slide.title,
                description: content[`slide_${slideNumber}_description`] || slide.description
              };
            });
          });
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };

    fetchContent();
  }, []);

  const handleEditStart = () => {
    setIsAutoPlayEnabled(false);
    setIsEditing(true);
  };

  const handleEditEnd = () => {
    setIsAutoPlayEnabled(true);
    setIsEditing(false);
  };

  const handleContentUpdate = (key: string, newContent: string) => {
    setSlides(currentSlides => {
      const newSlides = [...currentSlides];
      const slideIndex = currentSlide;
      
      if (key.includes('title')) {
        newSlides[slideIndex] = {
          ...newSlides[slideIndex],
          title: newContent
        };
      } else if (key.includes('description')) {
        newSlides[slideIndex] = {
          ...newSlides[slideIndex],
          description: newContent
        };
      }
      
      return newSlides;
    });
  };

  const nextSlide = useCallback(() => {
    if (currentSlide === heroSlides.length - 1) {
      setPage([0, -1]);
      setCurrentSlide(0);
    } else {
      setPage([currentSlide + 1, 1]);
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoPlayEnabled && !isEditing) {
      interval = setInterval(nextSlide, 15000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlayEnabled, nextSlide, isEditing]);

  const handleDotClick = (index: number) => {
    const direction = index > currentSlide ? 1 : -1;
    setPage([index, direction]);
    setCurrentSlide(index);
    setIsAutoPlayEnabled(false);
  };

  return (
    <Box>
      {/* Hero Section med Karusell */}
      <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={page}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 }
            }}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%'
            }}
          >
            <Box
              sx={{
                height: '100%',
                backgroundImage: 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: isAuthenticated ? 'transparent' : 'rgba(0, 0, 0, 0.3)',
                  pointerEvents: 'none',
                  zIndex: 2
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1
                }}
              >
                {isAuthenticated && isAdmin ? (
                  <EditableContent
                    content=""
                    pageName="home"
                    contentKey={`slide_${currentSlide + 1}_image`}
                    type="image"
                    imageUrl={heroSlides[currentSlide].image}
                    sx={{
                      width: '100%',
                      height: '100%'
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={heroSlides[currentSlide].image}
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                )}
              </Box>

              <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 3 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <EditableContent
                    content={slides[currentSlide].title}
                    pageName="home"
                    contentKey={`slide_${currentSlide + 1}_title`}
                    type="text"
                    onEditStart={handleEditStart}
                    onEditEnd={handleEditEnd}
                    onUpdate={(newContent) => handleContentUpdate(`slide_${currentSlide + 1}_title`, newContent)}
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                      fontWeight: 500,
                      mb: 2,
                      // fontFamily: 'Playfair Display, serif'
                    }}
                  />
                  <EditableContent
                    content={slides[currentSlide].description}
                    pageName="home"
                    contentKey={`slide_${currentSlide + 1}_description`}
                    type="text"
                    onUpdate={(newContent) => handleContentUpdate(`slide_${currentSlide + 1}_description`, newContent)}
                    sx={{
                      fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                      maxWidth: '800px',
                      margin: '0 auto',
                      mb: 4,
                      textShadow: !isAuthenticated ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none'
                    }}
                  />
                </motion.div>
              </Container>

              {/* Kontakta Oss-knapp */}
              <Box 
                sx={{ 
                  position: 'absolute',
                  bottom: { xs: '20%', sm: '25%' },
                  left: 0,
                  right: 0,
                  zIndex: 3
                }}
              >
                <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setIsModalOpen(true)}
                      sx={{
                        backgroundColor: theme.palette.custom.orange,
                        padding: { 
                          xs: '12px 24px',
                          sm: '16px 48px' 
                        },
                        fontSize: { 
                          xs: '1.1rem',
                          sm: '1.2rem',
                          md: '1.4rem' 
                        },
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        '&:hover': {
                          backgroundColor: theme.palette.custom.orange,
                          filter: 'brightness(0.9)'
                        }
                      }}
                    >
                      Kontakta Oss
                    </Button>
                  </motion.div>
                </Container>
              </Box>

              {/* Indikatorer */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 2,
                  zIndex: 2
                }}
              >
                {heroSlides.map((slide, index) => (
                  <Box
                    key={slide.id}
                    onClick={() => handleDotClick(index)}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: currentSlide === index ? theme.palette.custom.orange : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.2)',
                        backgroundColor: currentSlide === index ? theme.palette.custom.orange : 'rgba(255, 255, 255, 0.8)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>

      <ContactModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Box>
  );
};

export default Home; 